import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" data-testid="header-login-link" className="text-[#003366] font-medium hover:text-[#008080] transition-colors">
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-[#008080] transition-colors" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Pricing Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-[900] text-[#003366] mb-4 tracking-tight" data-testid="pricing-heading">
              Pricing
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              One comprehensive assessment to understand your DPDP compliance readiness
            </p>
          </div>

          <div className="max-w-xl mx-auto">
            <div 
              className="bg-white border-2 border-[#003366] rounded-sm p-10 shadow-[0_4px_24px_rgba(0,51,102,0.12)]"
              data-testid="pricing-card"
            >
              <div className="text-center mb-8">
                <span className="uppercase tracking-[0.2em] text-xs font-[800] text-[#008080]">
                  One-Time Assessment
                </span>
                <h2 className="text-2xl font-[800] text-[#003366] mt-2">DPDP Readiness Assessment</h2>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "83-question structured assessment",
                  "15 DPDP compliance pillars",
                  "Weighted readiness score (0–100)",
                  "Pillar-wise RAG compliance heatmap",
                  "SDF (Significant Data Fiduciary) risk assessment",
                  "DPDP evidence planner for audit readiness",
                  "Full gap register with DPDP Act and Rules 2025 references",
                  "12–18 month phased compliance roadmap",
                  "Penalty risk exposure analysis",
                  "Evidence artefacts checklist for audit readiness",
                  "Industry-specific observations (Pharma, BFSI, Healthcare, Retail, IT/SaaS, Manufacturing, Education)"
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`pricing-feature-${index + 1}`}>
                    <CheckCircle className="h-5 w-5 text-[#008080] shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/checkout">
                <Button 
                  data-testid="buy-now-btn"
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white text-lg py-6 rounded-sm btn-hover"
                >
                  Buy Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <img src={LOGO_URL} alt="CodeXero" className="h-8 brightness-0 invert" />
              <a 
                href="https://www.codexero.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-white transition-colors"
              >
                www.codexero.ai
              </a>
              <a 
                href="mailto:support@codexero.ai"
                className="text-slate-300 hover:text-white transition-colors"
              >
                support@codexero.ai
              </a>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <span>© CodeXero {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
